import { notify } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { NotificationKeys } from '../../../../../../../../../enums/notificationKeys'
import { APIError } from '../../../../../../../../../utils'
import { useFetchNextAvailableAccountNumber } from '../../../../../hooks/useFetchNextAvailableAccountNumber'
import { getAccountNumberValidationMessage } from '../../../utils/getAccountNumberValidationMessage'
import { useAccountNumberValidation } from './useAccountNumberValidation'

interface UseAccountNumberFieldOptions {
  clearAccountNumberError: () => void
  getAccountNumber: () => number | null
  organizationId: string | undefined
  predefinedAccountId: string
  setAccountNumber: (accountNumber: number | null) => void
  setAccountNumberError: (message: string) => void
}

export const useAccountNumberField = ({
  clearAccountNumberError,
  getAccountNumber,
  organizationId,
  predefinedAccountId,
  setAccountNumber,
  setAccountNumberError,
}: UseAccountNumberFieldOptions) => {
  const [baseAccountNumber, setBaseAccountNumber] = useState<number | null>(null)
  const [isManualEntryEnabled, setIsManualEntryEnabled] = useState(false)
  const { t } = useTranslation()

  const accountNumberCaption =
    isManualEntryEnabled || baseAccountNumber === null
      ? undefined
      : t('coa.account_from_base_form.account_number.caption', { accountNumber: baseAccountNumber })

  const handleNextAvailableAccountNumberError = useCallback(
    (error: APIError) => {
      if (isManualEntryEnabled) {
        return
      }

      const fieldErrorMessage = getAccountNumberValidationMessage(error)
      if (fieldErrorMessage) {
        setAccountNumberError(fieldErrorMessage)
        return
      }

      notify({
        id: NotificationKeys.AccountFromTemplateCreate,
        message: error.body?.errorMessage || t('error_saving'),
        variant: 'error',
      })
      trackError(error)
    },
    [isManualEntryEnabled, setAccountNumberError, t],
  )

  const {
    isFetching: isFetchingAccountNumber,
    nextAvailableAccountNumber,
    refetch: refreshAccountNumber,
  } = useFetchNextAvailableAccountNumber({
    onError: handleNextAvailableAccountNumberError,
    organizationId,
    predefinedAccountId,
  })

  const {
    clearValidation,
    isProcessing: isValidating,
    validateAccountNumberIfNeeded,
  } = useAccountNumberValidation({
    automaticAccountNumber: nextAvailableAccountNumber,
    clearAccountNumberError,
    getAccountNumber,
    isManualEntryEnabled,
    organizationId,
    setAccountNumberError,
  })

  const handleAccountNumberChange = useCallback(() => {
    clearValidation()
  }, [clearValidation])

  const handleAccountNumberBlur = useCallback(
    (accountNumberToValidate: number | null) => {
      validateAccountNumberIfNeeded(accountNumberToValidate)
    },
    [validateAccountNumberIfNeeded],
  )

  const handleManualEntryChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const isEnabled = event.target.checked
      setIsManualEntryEnabled(isEnabled)
      clearValidation()

      if (!isEnabled) {
        setAccountNumber(nextAvailableAccountNumber ?? null)
      }
    },
    [clearValidation, nextAvailableAccountNumber, setAccountNumber],
  )

  const selectBaseAccount = useCallback(
    (selectedBaseAccountNumber: number | null) => {
      setBaseAccountNumber(selectedBaseAccountNumber)
      setIsManualEntryEnabled(false)
      clearValidation()
      setAccountNumber(null)
    },
    [clearValidation, setAccountNumber],
  )

  const reset = useCallback(() => {
    setBaseAccountNumber(null)
    setIsManualEntryEnabled(false)
    clearValidation()
  }, [clearValidation])

  useEffect(() => {
    if (isManualEntryEnabled || !predefinedAccountId || nextAvailableAccountNumber === undefined) {
      return
    }

    clearValidation()
    setAccountNumber(nextAvailableAccountNumber)
  }, [clearValidation, isManualEntryEnabled, nextAvailableAccountNumber, predefinedAccountId, setAccountNumber])

  return {
    accountNumberCaption,
    handleAccountNumberBlur,
    handleAccountNumberChange,
    handleManualEntryChange,
    isFetchingAccountNumber,
    isManualEntryEnabled,
    isValidating,
    refreshAccountNumber,
    reset,
    selectBaseAccount,
    validateAccountNumberIfNeeded,
  }
}
